import { ApprovalSection } from "@/components/Approval";
import { useEffect, useState } from "react";
import { Link, getRouteApi, useNavigate } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { unwrap, type Schemas } from "@tern/api-client";
import { Bell, BellOff, Link2, MoreHorizontal, Plus, Trash2, X } from "lucide-react";
import * as DropdownMenu from "@radix-ui/react-dropdown-menu";
import { Comments } from "@/components/Comments";
import { PageHeader } from "@/components/AppShell";
import { CopyUidButton } from "@/components/CopyUidButton";
import { EmptyState } from "@/components/EmptyState";
import { IssueCreateDialog } from "@/components/IssueCreateDialog";
import { AssigneePicker, DueDateButton, EstimatePicker, LabelPicker, PriorityPicker, StatePicker, TaskPicker } from "@/components/IssueProps";
import { Markdown, MarkdownEditor } from "@/components/Markdown";
import { Picker } from "@/components/Picker";
import { Avatar, PriorityIcon, StateIcon } from "@/components/PropertyIcons";
import { api, errorMessage } from "@/lib/api";
import { formatRelative, PRIORITIES } from "@/lib/issues";
import { deleteIssue, liveIssues, patchIssue, restoreIssue, useProject, useProjectStore, type Issue, type ProjectData } from "@/lib/store";
import { toast } from "@/lib/toast";
import { setSubscription, subscriptionQuery } from "@/lib/inbox";
import { DocBackrefs } from "@/components/DocBackrefs";
import { ProjectLoading, useLoadProject } from "./ProjectHome";

const route = getRouteApi("/$ws/issue/$identifier");

type Activity = Schemas["Activity"];

export function IssueDetailPage() {
  const { ws, identifier } = route.useParams();
  return <IssueByIdentifier ws={ws} identifier={identifier} />;
}

/** Loads the owning project and renders the issue; `embedded` hides the page header (Inbox pane). */
export function IssueByIdentifier({ ws, identifier, embedded }: { ws: string; identifier: string; embedded?: boolean }) {
  const { t } = useTranslation();
  const key = identifier.split("-")[0]!.toUpperCase();
  useLoadProject(ws, key);
  const d = useProject(ws, key);
  const number = Number(identifier.split("-")[1]);
  const issue = d ? Object.values(d.issues).find((i) => i.number === number) : undefined;

  // Identifier may be an alias (renamed key / moved issue); resolve through the API.
  const resolved = useQuery({
    queryKey: ["issue-by-id", ws, identifier],
    queryFn: async () => unwrap(await api.GET("/api/workspaces/{slug}/issues/{identifier}", { params: { path: { slug: ws, identifier } } })),
    enabled: !!d && !issue,
    retry: false,
  });
  useEffect(() => {
    if (resolved.data) useProjectStore.getState().applyIssue(resolved.data);
  }, [resolved.data]);

  if (!d) return <ProjectLoading ws={ws} kkey={key} />;
  const live = issue ?? (resolved.data ? d.issues[resolved.data.id] : undefined);
  if (!live) {
    if (resolved.isPending && !issue) return <div className="flex flex-1 items-center justify-center text-muted">{t("common.loading")}</div>;
    return (
      <div className="flex flex-1 items-center justify-center">
        <EmptyState title={t("issue.notFound")} body={t("issue.notFoundBody")} actions={<Link to="/$ws/project/$key/issues/$tab" params={{ ws, key, tab: "all" }} className="btn">{t("common.back")}</Link>} />
      </div>
    );
  }
  return <IssueView d={d} issue={live} embedded={embedded} />;
}

function IssueView({ d, issue, embedded }: { d: ProjectData; issue: Issue; embedded?: boolean }) {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const id = `${d.project.key}-${issue.number}`;
  const canEdit = !!d.project.role;
  const [subOpen, setSubOpen] = useState(false);
  const children = liveIssues(d).filter((i) => i.parent_id === issue.id);
  const parent = issue.parent_id ? d.issues[issue.parent_id] : undefined;

  const remove = async () => {
    try {
      await deleteIssue(issue);
      toast(t("issue.deleted", { id }), { action: { label: t("common.undo"), run: () => void restoreIssue(issue) } });
      await navigate({ to: "/$ws/project/$key/issues/$tab", params: { ws: d.slug, key: d.project.key, tab: "all" } });
    } catch (e) {
      toast(errorMessage(e), { tone: "error" });
    }
  };

  return (
    <>
      <PageHeader
        besideCrumbs={<CopyUidButton uid={issue.id} />}
        embedded={embedded}
        crumbs={[
          <Link key="p" to="/$ws/project/$key/issues/$tab" params={{ ws: d.slug, key: d.project.key, tab: "all" }} className="hover:text-fg">{d.project.name}</Link>,
          <span key="id" className="font-mono">{id}</span>,
        ]}
        right={
          <>
            <button className="btn btn-ghost h-7 w-7 justify-center p-0" title={t("issue.copyLink")} onClick={() => void navigator.clipboard?.writeText(location.href).then(() => toast(t("issue.copied")))}>
              <Link2 className="h-4 w-4" />
            </button>
            <SubscribeButton issueId={issue.id} />
            {canEdit && (
              <DropdownMenu.Root>
                <DropdownMenu.Trigger asChild>
                  <button className="btn btn-ghost h-7 w-7 justify-center p-0">
                    <MoreHorizontal className="h-4 w-4" />
                  </button>
                </DropdownMenu.Trigger>
                <DropdownMenu.Portal>
                  <DropdownMenu.Content align="end" sideOffset={4} className="z-50 w-[200px] rounded-lg border border-border bg-elevated p-1 shadow-xl">
                    <DropdownMenu.Item className="flex h-8 cursor-default items-center gap-2 rounded-md px-2 text-[13px] outline-none data-[highlighted]:bg-hover" onSelect={() => setSubOpen(true)}>
                      <Plus className="h-3.5 w-3.5" /> {t("issue.addSubIssue")}
                    </DropdownMenu.Item>
                    <DropdownMenu.Item className="flex h-8 cursor-default items-center gap-2 rounded-md px-2 text-[13px] text-danger outline-none data-[highlighted]:bg-hover" onSelect={() => void remove()}>
                      <Trash2 className="h-3.5 w-3.5" /> {t("common.delete")}
                    </DropdownMenu.Item>
                  </DropdownMenu.Content>
                </DropdownMenu.Portal>
              </DropdownMenu.Root>
            )}
          </>
        }
      />
      <div className="flex flex-1 overflow-hidden">
        <div className="flex-1 overflow-y-auto">
          <div className="mx-auto max-w-[760px] px-8 py-6">
            {parent && (
              <Link to="/$ws/issue/$identifier" params={{ ws: d.slug, identifier: `${d.project.key}-${parent.number}` }} className="mb-2 inline-flex items-center gap-1.5 text-[12px] text-muted hover:text-fg">
                <StateIcon state={d.states[parent.state_id]} className="h-3 w-3" /> {d.project.key}-{parent.number} · {parent.title}
              </Link>
            )}
            <TitleEditor issue={issue} canEdit={canEdit} />
            <DescriptionEditor issue={issue} canEdit={canEdit} />

            <section className="mt-8">
              <div className="mb-2 flex items-center justify-between">
                <h3 className="text-[13px] font-semibold text-muted">{t("issue.subIssues")} {children.length > 0 && <span className="text-faint">· {children.filter((c) => ["completed", "canceled"].includes(d.states[c.state_id]?.type ?? "")).length}/{children.length}</span>}</h3>
                {canEdit && (
                  <button className="btn btn-ghost h-7 text-[12.5px]" onClick={() => setSubOpen(true)}>
                    <Plus className="h-3.5 w-3.5" /> {t("issue.addSubIssue")}
                  </button>
                )}
              </div>
              {children.length > 0 && (
                <ul className="divide-y divide-border rounded-lg border border-border">
                  {children.map((c) => (
                    <li key={c.id} className="flex h-9 items-center gap-2 px-3 text-[13px]">
                      <PriorityPicker issue={c} compact />
                      <span className="font-mono text-[11.5px] text-muted">{d.project.key}-{c.number}</span>
                      <StatePicker d={d} issue={c} compact />
                      <Link to="/$ws/issue/$identifier" params={{ ws: d.slug, identifier: `${d.project.key}-${c.number}` }} className="flex-1 truncate hover:underline">{c.title}</Link>
                      <AssigneePicker d={d} issue={c} compact />
                    </li>
                  ))}
                </ul>
              )}
            </section>

            <ActivityFeed d={d} issue={issue} />
          </div>
        </div>

        <aside className="w-[300px] shrink-0 overflow-y-auto border-l border-border p-4">
          <ApprovalSection d={d} item={issue} status={d.states[issue.state_id]?.type ?? ""} onChange={(config) => patchIssue(issue, config)} onApprove={() => patchIssue(issue, { state_id: Object.values(d.states).find((s) => s.type === "completed" && !s.deleted_at)!.id })} />
          <Prop label={t("issue.status")}><StatePicker d={d} issue={issue} /></Prop>
          <Prop label={t("issue.priority")}><PriorityPicker issue={issue} /></Prop>
          <Prop label={t("issue.assignee")}><AssigneePicker d={d} issue={issue} /></Prop>
          <Prop label={t("issue.labels")}><LabelPicker d={d} issue={issue} /></Prop>
          <Prop label={t("issue.dueDate")}><DueDateButton issue={issue} /></Prop>
          {d.project.estimate_enabled && <Prop label={t("issue.estimate")}><EstimatePicker issue={issue} /></Prop>}
          <Prop label={t("issue.task")}><TaskPicker d={d} issue={issue} /></Prop>
          {issue.task_id && d.tasks[issue.task_id]?.milestone_id && (
            <Prop label={t("nav.milestone")}><span className="inline-flex h-6 items-center px-1.5 text-[12.5px] text-muted">{d.milestones[d.tasks[issue.task_id]!.milestone_id!]?.name}</span></Prop>
          )}
          <Prop label={t("issue.parent")}><ParentPicker d={d} issue={issue} /></Prop>
          <Relations d={d} issue={issue} canEdit={canEdit} />
          <DocBackrefs d={d} entity={{ type: "issue", id: issue.id }} />
          <div className="mt-6 border-t border-border pt-3 text-[11.5px] text-faint">
            <div>{t("issue.createdBy", { name: d.users[issue.creator_id]?.name ?? "?", when: formatRelative(issue.created_at, i18n.language) })}</div>
            <div className="mt-0.5">{t("issue.updated", { when: formatRelative(issue.updated_at, i18n.language) })}</div>
          </div>
        </aside>
      </div>
      <IssueCreateDialog d={d} open={subOpen} onOpenChange={setSubOpen} defaults={{ parent_id: issue.id }} />
    </>
  );
}

function SubscribeButton({ issueId }: { issueId: string }) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const sub = useQuery(subscriptionQuery(issueId));
  const on = sub.data?.subscribed ?? false;
  const toggle = useMutation({
    mutationFn: () => setSubscription(issueId, !on),
    onSuccess: (s) => {
      qc.setQueryData(subscriptionQuery(issueId).queryKey, s);
      toast(s.subscribed ? t("issue.subscribed") : t("issue.unsubscribed"));
    },
    onError: (e) => toast(errorMessage(e), { tone: "error" }),
  });
  return (
    <button className={`btn btn-ghost h-7 w-7 justify-center p-0 ${on ? "text-accent" : ""}`} title={on ? t("issue.unsubscribe") : t("issue.subscribe")} onClick={() => toggle.mutate()} disabled={sub.isPending}>
      {on ? <Bell className="h-4 w-4" /> : <BellOff className="h-4 w-4" />}
    </button>
  );
}

function Prop({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="mb-2 flex min-h-[28px] items-start gap-2">
      <span className="w-[84px] shrink-0 pt-1 text-[12px] text-muted">{label}</span>
      <div className="min-w-0 flex-1">{children}</div>
    </div>
  );
}

function TitleEditor({ issue, canEdit }: { issue: Issue; canEdit: boolean }) {
  const [value, setValue] = useState(issue.title);
  // Reset the draft when the server title changes (adjust-state-during-render pattern).
  const [seenTitle, setSeenTitle] = useState(issue.title);
  if (seenTitle !== issue.title) {
    setSeenTitle(issue.title);
    setValue(issue.title);
  }
  const save = () => {
    const v = value.trim();
    if (v && v !== issue.title) void patchIssue(issue, { title: v }).catch((e) => toast(errorMessage(e), { tone: "error" }));
    else setValue(issue.title);
  };
  return (
    <textarea
      className="w-full resize-none bg-transparent text-[22px] font-semibold leading-tight text-fg outline-none"
      rows={Math.max(1, Math.ceil(value.length / 50))}
      value={value}
      readOnly={!canEdit}
      onChange={(e) => setValue(e.target.value)}
      onBlur={save}
      onKeyDown={(e) => {
        if (e.key === "Enter") {
          e.preventDefault();
          (e.target as HTMLTextAreaElement).blur();
        }
      }}
    />
  );
}

function DescriptionEditor({ issue, canEdit }: { issue: Issue; canEdit: boolean }) {
  const { t } = useTranslation();
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(issue.description);
  const save = (markdown = draft) => {
    setEditing(false);
    if (markdown !== issue.description) void patchIssue(issue, { description: markdown }).catch((e) => toast(errorMessage(e), { tone: "error" }));
  };
  if (editing) return <div className="mt-3"><MarkdownEditor projectId={issue.project_id} value={draft} onChange={setDraft} onSave={save} saveOnBlur autoFocus minRows={6} placeholder={t("issue.descriptionPlaceholder")} /></div>;
  return (
    <div
      className={`mt-3 min-h-[40px] rounded-md ${canEdit ? "-m-1 cursor-text p-1 hover:bg-hover/40" : ""}`}
      onClick={() => {
        if (canEdit) {
          setDraft(issue.description);
          setEditing(true);
        }
      }}
    >
      {issue.description ? <Markdown source={issue.description} /> : <span className="text-[13px] text-faint">{t("issue.descriptionPlaceholder")}</span>}
    </div>
  );
}

function ParentPicker({ d, issue }: { d: ProjectData; issue: Issue }) {
  const { t } = useTranslation();
  const parent = issue.parent_id ? d.issues[issue.parent_id] : undefined;
  const candidates = liveIssues(d).filter((i) => i.id !== issue.id && i.parent_id !== issue.id);
  return (
    <Picker
      value={issue.parent_id ?? "none"}
      placeholder={t("issue.setParent")}
      items={[{ id: "none", label: t("issue.noParent") }, ...candidates.map((i) => ({ id: i.id, label: `${d.project.key}-${i.number} ${i.title}`, icon: <StateIcon state={d.states[i.state_id]} className="h-3 w-3" /> }))]}
      onSelect={(v) => void patchIssue(issue, { parent_id: v === "none" ? null : v }).catch((e) => toast(errorMessage(e), { tone: "error" }))}
      trigger={<button className="inline-flex h-6 max-w-full items-center gap-1.5 rounded-md border border-transparent px-1.5 text-[12.5px] text-muted hover:border-border-strong hover:bg-hover hover:text-fg"><span className="truncate">{parent ? `${d.project.key}-${parent.number} ${parent.title}` : t("issue.noParent")}</span></button>}
    />
  );
}

function Relations({ d, issue, canEdit }: { d: ProjectData; issue: Issue; canEdit: boolean }) {
  const { t } = useTranslation();
  const rels = Object.values(d.relations).filter((r) => r.from_issue_id === issue.id || r.to_issue_id === issue.id);
  const rows = rels.map((r) => {
    const otherId = r.from_issue_id === issue.id ? r.to_issue_id : r.from_issue_id;
    const kind = r.type === "blocks" ? (r.from_issue_id === issue.id ? "blocks" : "blockedBy") : r.type === "duplicate" ? (r.from_issue_id === issue.id ? "duplicateOf" : "duplicatedBy") : "related";
    return { r, other: d.issues[otherId], kind };
  });
  const add = async (kind: string, toId: string) => {
    try {
      const rel = unwrap(await api.POST("/api/issues/{id}/relations", { params: { path: { id: issue.id } }, body: { to_issue_id: toId, type: kind } }));
      useProjectStore.getState().applyRelation(rel, "created");
    } catch (e) {
      toast(errorMessage(e), { tone: "error" });
    }
  };
  const remove = async (rid: string) => {
    try {
      const rel = unwrap(await api.DELETE("/api/relations/{id}", { params: { path: { id: rid } } }));
      useProjectStore.getState().applyRelation(rel, "deleted");
    } catch (e) {
      toast(errorMessage(e), { tone: "error" });
    }
  };
  return (
    <div className="mt-4 border-t border-border pt-3">
      <div className="mb-1 text-[12px] text-muted">{t("issue.relations")}</div>
      {rows.length === 0 && <div className="text-[12px] text-faint">{t("issue.noRelations")}</div>}
      <ul className="flex flex-col gap-1">
        {rows.map(({ r, other, kind }) =>
          other ? (
            <li key={r.id} className="group flex items-center gap-1.5 text-[12.5px]">
              <span className="w-[76px] shrink-0 text-[11.5px] text-faint">{t(`relation.${kind}`)}</span>
              <StateIcon state={d.states[other.state_id]} className="h-3 w-3" />
              <Link to="/$ws/issue/$identifier" params={{ ws: d.slug, identifier: `${d.project.key}-${other.number}` }} className="min-w-0 flex-1 truncate hover:underline">
                <span className="font-mono text-muted">{d.project.key}-{other.number}</span> {other.title}
              </Link>
              {canEdit && (
                <button className="opacity-0 group-hover:opacity-100" onClick={() => void remove(r.id)}>
                  <X className="h-3 w-3 text-muted" />
                </button>
              )}
            </li>
          ) : null,
        )}
      </ul>
      {canEdit && (
        <div className="mt-1.5 flex flex-wrap gap-1">
          <AddRelationPicker d={d} issue={issue} kind="blocks" onAdd={add} />
          <AddRelationPicker d={d} issue={issue} kind="related" onAdd={add} />
          <AddRelationPicker d={d} issue={issue} kind="duplicate" onAdd={add} />
        </div>
      )}
    </div>
  );
}

function AddRelationPicker({ d, issue, kind, onAdd }: { d: ProjectData; issue: Issue; kind: "blocks" | "related" | "duplicate"; onAdd: (kind: string, toId: string) => Promise<void> }) {
  const { t } = useTranslation();
  const others = liveIssues(d).filter((i) => i.id !== issue.id);
  return (
    <Picker
      value={null}
      placeholder={t(`relation.${kind}`)}
      items={others.map((i) => ({ id: i.id, label: `${d.project.key}-${i.number} ${i.title}`, icon: <StateIcon state={d.states[i.state_id]} className="h-3 w-3" /> }))}
      onSelect={(v) => void onAdd(kind, v)}
      trigger={<button className="rounded px-1.5 py-0.5 text-[11.5px] text-muted hover:bg-hover hover:text-fg">+ {t(`relation.${kind}`)}</button>}
    />
  );
}

function ActivityFeed({ d, issue }: { d: ProjectData; issue: Issue }) {
  const { t } = useTranslation();
  const activities = useQuery({ queryKey: ["issue", issue.id, "activities"], queryFn: async () => unwrap(await api.GET("/api/issues/{id}/activities", { params: { path: { id: issue.id } } })) });
  return <>
    <Comments d={d} entityType="issue" entityId={issue.id} />
    <section className="mt-8">
      <h3 className="mb-3 text-[13px] font-semibold text-muted">{t("issue.activity")}</h3>
      <div className="flex flex-col gap-2">{activities.data?.filter(a => a.action !== "commented").map(a => <ActivityLine key={a.id} d={d} a={a} />)}</div>
    </section>
  </>;
}

function ActivityLine({ d, a }: { d: ProjectData; a: Activity }) {
const { t, i18n } = useTranslation();
  const actor = d.users[a.actor_id]?.name ?? "?";
  const when = formatRelative(a.created_at, i18n.language);
  const render = (field: string | null | undefined, v: unknown): React.ReactNode => {
    if (v == null) return <span className="text-faint">—</span>;
    switch (field) {
      case "state": {
        const s = d.states[String(v)];
        return <span className="inline-flex items-center gap-1"><StateIcon state={s} className="h-3 w-3" />{s?.name ?? "?"}</span>;
      }
      case "assignee":
        return d.users[String(v)]?.name ?? t("issue.unassigned");
      case "priority":
        return <span className="inline-flex items-center gap-1"><PriorityIcon priority={Number(v)} className="h-3 w-3" />{t(`priority.${PRIORITIES[Number(v)]?.key ?? "none"}`)}</span>;
      case "labels":
        return (v as string[]).map((l) => d.labels[l]?.name ?? "?").join(", ") || <span className="text-faint">—</span>;
      case "parent": {
        const p = d.issues[String(v)];
        return p ? `${d.project.key}-${p.number}` : "?";
      }
      default:
        return String(v);
    }
  };
  let text: React.ReactNode;
  if (a.action === "created") text = t("activity.created");
  else if (a.action === "deleted") text = t("activity.deleted");
  else if (a.action === "restored") text = t("activity.restored");
  else if (a.action === "related") text = t("activity.related", { type: t(`relation.${a.field}`) });
  else if (a.field === "approval_required") text = `${t("activity.changed", { field: t("approval.settings") })} → ${t(a.to_value ? "approval.required" : "approval.none")}`;
  else if (a.field === "approvers") text = `${t("activity.changed", { field: t("approval.select") })} → ${Array.isArray(a.to_value) ? a.to_value.map((id) => d.users[String(id)]?.name ?? t("approval.formerMember")).join(", ") : "—"}`;
  else if (a.field === "approved_by") text = t(a.to_value ? "approval.activityApproved" : "approval.activityReset");
  else if (a.field === "description") text = t("activity.editedDescription");
  else if (a.field === "title") text = t("activity.renamed", { to: String(a.to_value) });
  else text = <>{t("activity.changed", { field: t(`issue.${fieldKey(a.field)}`) })} {render(a.field, a.from_value)} → {render(a.field, a.to_value)}</>;
  return (
    <div className="flex items-center gap-2 pl-1 text-[12.5px] text-muted">
      <Avatar user={d.users[a.actor_id]} size={16} />
      <span className="font-medium text-fg">{actor}</span>
      <span className="min-w-0 truncate">{text}</span>
      <span className="ml-auto shrink-0 text-[11.5px] text-faint">{when}</span>
    </div>
  );
}
function fieldKey(f: string | null | undefined) {
  switch (f) {
    case "state": return "status";
    case "due_date": return "dueDate";
    case "archived": return "archived";
    default: return f ?? "";
  }
}
